import { set } from 'lodash'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'

import Page from '@/components/Page'
import { getOrgAdminRole, isRequestComplete } from '@/store/selectors'

import { bulkAddUsers, cancelUpload, finishedProcessing, reset, setUsersToAddList, startProcessing } from './actions'
import messages from './messages'
import {
  getCreatedUsers,
  getCsvData,
  getFailedUsers,
  getInfoUsers,
  getIsProcessingCsv,
  getUsersToAdd
} from './selectors'

import './index.scss'

import fileSaver from 'file-saver'
import Papa from 'papaparse'
import AnimateHeight from 'react-animate-height'
import { useHistory, useLocation } from 'react-router-dom'
import { usePrevious } from 'react-use'
import saveTheCsv from 'save-csv'

import { getCurrentOrganizationId } from '@/components/App/selectors'
import Form from '@/components/Form'
import FileDragAndDrop from '@/components/Form/FileDragAndDrop'
import { Csv, TriangleDown2 } from '@/components/Icons'
import NumberAnimation from '@/components/NumberAnimation'
import PaginationTable from '@/components/PaginationTable'
import { fetchAttributes } from '@/pages/OrganizationUserAttributesPage/actions'
import { FETCH_ATTRIBUTES } from '@/pages/OrganizationUserAttributesPage/actionTypes'
import attributeShape from '@/pages/OrganizationUserAttributesPage/attributeShape'
import { getAttributesInOrder } from '@/pages/OrganizationUserAttributesPage/selectors'
import { getUserSchema } from '@/pages/OrganizationUserEditPage/UserAccountView/user-schema'
import cn from '@/utilities/cn'

import { createdColumns, userColumns, validationColumns } from './columns'
import Tooltip from './Tooltip'
import { mapSubmitValues } from './utils'

//Friendly name map
const fieldMap = {
  userName: 'email',
  username: 'email',
  givenName: 'name.givenName',
  familyName: 'name.familyName',
  displayName: 'displayName',
  'first name': 'name.givenName',
  'last name': 'name.familyName',
  'display name': 'displayName'
}

const minimumRequiredFields = ['email', 'name.givenName', 'name.familyName']

const simplifiedFields = [{ name: 'email' }, { name: 'name.givenName' }, { name: 'name.familyName' }]
const allFields = [
  { name: 'email' },
  { name: 'externalId' },
  { name: 'name.givenName' },
  { name: 'name.familyName' },
  { name: 'name.formatted' },
  { name: 'name.honorificPrefix' },
  { name: 'name.honorificSuffix' },
  { name: 'name.middleName' },
  { name: 'nickName' },
  { name: 'userType' },
  { name: 'displayName' },
  { name: 'title' },
  { name: 'timezone' },
  { name: 'locale' },
  { name: 'preferredLanguage' },
  { name: 'profileUrl' },
  { name: 'enterpriseUser.employeeNumber' },
  { name: 'enterpriseUser.costCenter' },
  { name: 'enterpriseUser.organization' },
  { name: 'enterpriseUser.department' },
  { name: 'enterpriseUser.division' },
  { name: 'enterpriseUser.manager.value' },
  { name: 'enterpriseUser.manager.displayName' },
  { name: 'isOrgAdmin' },
  { name: 'addresses[0].locality' },
  { name: 'addresses[0].postalCode' },
  { name: 'addresses[0].region' },
  { name: 'addresses[0].streetAddress' },
  { name: 'addresses[0].country' },
  { name: 'addresses[0].formatted' },
  { name: 'addresses[0].type' },
  { name: 'addresses[0].primary' },
  { name: 'emails[0].value' },
  { name: 'emails[0].type' },
  { name: 'emails[0].display' },
  { name: 'emails[0].primary' },
  { name: 'phoneNumbers[0].value' },
  { name: 'phoneNumbers[0].type' },
  { name: 'phoneNumbers[0].display' },
  { name: 'phoneNumbers[0].primary' },
  { name: 'photos[0].value' },
  { name: 'photos[0].type' },
  { name: 'photos[0].display' },
  { name: 'photos[0].primary' }
]

function OrganizationUsersBulkAddPage(props: any) {
  const intl = useIntl()
  const history = useHistory()
  const location = useLocation()

  const [state, _setState] = useState({
    validationIssues: [],
    processing: false,
    allKnownFields: [...allFields],
    cancelling: false,
    createdPage: 1,
    infoPage: 1,
    issuesPage: 1,
    failedPage: 1
  })
  const setState = (update: Partial<Parameters<typeof _setState>[0]>) => {
    return _setState(state => ({ ...state, ...(typeof update === 'function' ? update(state) : update) }))
  }

  const baseUserUrl = `${window.location.origin}${process.env.PUBLIC_URL}/organization/users/`.replaceAll('//', '/')

  const userSchema = useRef(getUserSchema([]))
  const unmounting = useRef(false)
  const cancelled = useRef(false)

  // componentDidMount
  useEffect(() => {
    //just in case the use is somehow still uploading
    unmounting.current = false
    props.actions.cancelUpload()
    props.actions.reset()
    props.actions.fetchAttributes()

    if (props.attributes) {
      userSchema.current = getUserSchema(props.attributes)
      setState({ allKnownFields: [...allFields, ...getKnownCustomAttributeFields(props.attributes)] })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // componentDidUpdate
  const prevProps = usePrevious(props)
  useEffect(() => {
    if (!prevProps) return

    if (props.attributes !== prevProps.attributes && props.attributes) {
      userSchema.current = getUserSchema(props.attributes)
      setState({ allKnownFields: [...allFields, ...getKnownCustomAttributeFields(props.attributes)] })
    }
  }, [props, prevProps])

  // componentWillUnmount
  useEffect(() => {
    return () => {
      unmounting.current = true
      props.actions.cancelUpload()
      props.actions.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const goBack = () => {
    history.push(location.pathname.slice(0, Math.max(0, location.pathname.lastIndexOf('/'))))
  }

  const getKnownCustomAttributeFields = (attributes: any) => {
    return attributes.reduce((arr: any, { description, scimAttributeName, scimSchema }: any) => {
      if (scimSchema.startsWith('urn:ietf:params:scim:schemas:extension:workgrid:2.0:')) {
        arr.push({ name: `customAttributes.${scimAttributeName}`, description: description })
      }
      return arr
    }, [])
  }

  const toggleExpanded = (section: any) => {
    // @ts-expect-error
    setState(state => ({ [section]: !state[section] }))
  }

  const isUnKnownField = (field: any) => {
    const knownFieldMap = Object.fromEntries(state.allKnownFields.map(({ name }) => [name, true]))
    // @ts-expect-error
    return !fieldMap[field] && !knownFieldMap[field]
  }

  const getValidationMessage = (e: any) => {
    const { errors } = e
    return errors.join('. ')
  }

  const saveList = (list: any, filename: any) => {
    saveTheCsv(list, {
      filename,
      formatter: (cell: any) => {
        return typeof cell === 'string' ? '"' + cell.replaceAll('"', '""') + '"' : cell
      }
    })
  }

  const saveCreatedList = (e: any) => {
    e.preventDefault()
    saveList(
      props.created.map((field: any) => ({
        user: field.readableValue,
        message: `${baseUserUrl}${field.id}/edit`
      })),
      'created-users.csv'
    )
  }

  const saveInfoList = (e: any) => {
    e.preventDefault()
    saveList(
      props.info.map((field: any) => ({
        user: field.readableValue,
        message: field.message
      })),
      'created-users.csv'
    )
  }

  const saveFailedList = (e: any) => {
    e.preventDefault()
    const { csvData } = props
    const newCsvData = [
      [...csvData[0], 'failed message'],
      ...props.failed.map((field: any) => [...csvData[field.data._meta.index + 1], field.message])
    ]
    const csv = Papa.unparse(newCsvData)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    fileSaver.saveAs(blob, 'failed-users.csv')
  }

  const cancelUpload = (e: any) => {
    e.preventDefault()
    cancelled.current = true
    props.actions.cancelUpload()
    if (!unmounting) {
      setState({ cancelling: true })
    }
  }
  const onFileDrop = (files: any) => {
    if (files.length !== 1) return
    setState({ validationIssues: [], processing: true, cancelling: false })

    Papa.parse(files[0], {
      complete: async (results: any) => {
        const { data } = results
        await processCSVData(data)
      }
    })
  }

  const processCSVData = async (data: any) => {
    const [headers, ...users] = data
    const validationIssues: any = []
    const minimumFields = [...minimumRequiredFields]
    cancelled.current = false

    props.actions.startProcessing(data)

    //get the headers and map friendly names to the real field names and make sure we have all min required fields
    const fields = headers.map((header: any, index: any) => {
      // @ts-expect-error
      const field = { field: fieldMap[header] || header, isUnknown: isUnKnownField(header), index: index }
      const i = minimumFields.indexOf(field.field)
      if (i !== -1) {
        minimumFields.splice(i, 1)
      }
      return field
    })

    if (minimumFields.length > 1) {
      props.actions.reset()
      setState({
        validationIssues: [{ line: 0, message: 'Missing required fields ' + minimumFields.join(', ') }],
        processing: false
      })

      return
    }

    //use only known fields to map to and map the row to the user, and filter out any null values
    const knownFields = fields.filter((field: any) => !field.isUnknown)
    const userList = users
      .map((user: any, index: any) => {
        //skip blank lines
        if (user.reduce((acc: any, field: any) => acc && (!field || field.trim() === ''), true)) {
          return null
        }

        const newUser = {
          name: {},
          addresses: [],
          emails: [],
          phoneNumbers: [],
          photos: [],
          customAttributes: {},
          enterpriseUser: {},
          _meta: { index }
        }

        knownFields.forEach((field: any) => {
          //excel and other programs use 'TRUE' for boolean true which is needed for yup validation
          set(newUser, field.field, user[field.index])
        })
        return newUser
      })
      .filter(Boolean)

    // run the users through the yup validation before doing a final mapping to the request format
    const validatedUsers = userList.map((u: any) => {
      try {
        const out = userSchema.current.validateSync(u, { abortEarly: false })
        const mappedUser = mapSubmitValues(out, props.organizationId, props.attributes, props.orgAdminRole)
        mappedUser._meta = u._meta
        return mappedUser
      } catch (error) {
        validationIssues.push({
          line: u._meta.index + 2,
          message: getValidationMessage(error)
        })
        return null
      }
    })

    if (validationIssues.length > 0) {
      props.actions.reset()
      setState({ validationIssues, processing: false })
      return
    }

    props.actions.setUsersToAddList(validatedUsers)
    await props.actions.bulkAddUsers(validatedUsers)
    if (!unmounting) {
      props.actions.finishedProcessing()
      setState({ processing: false, cancelling: false })
    }
  }

  const onSelectCreated = (page: any) => {
    setState({ createdPage: page })
  }

  const onSelectInfo = (page: any) => {
    setState({ infoPage: page })
  }

  const onSelectIssues = (page: any) => {
    setState({ issuesPage: page })
  }

  const onSelectFailed = (page: any) => {
    setState({ failedPage: page })
  }

  const { created, failed, info, isProcessingCsv } = props
  const { validationIssues, processing, allKnownFields, cancelling } = state
  const isProcessing = processing || isProcessingCsv
  return (
    <Page className="organization-users-bulk-add-container">
      <Page.Header
        title={<FormattedMessage {...messages.pageTitle} />}
        goBack={goBack}
        backNavTitle={<FormattedMessage {...messages.back} />}
      />
      <Form>
        <FileDragAndDrop
          accept={['', 'csv', 'text/csv', 'application/vnd.ms-excel']}
          description={<FormattedMessage {...messages.bulkDescription} />}
          disabled={isProcessing}
          tooltip={<Tooltip simplifiedFields={simplifiedFields} allFields={allKnownFields} />}
          dropZoneDescription={
            isProcessing ? (
              <>
                <div className="bulk_upload__spinner" />
                <div className="dropzone-processing">
                  <FormattedMessage {...messages.addingUsers} />
                </div>
                <div>
                  {cancelling ? (
                    <FormattedMessage {...messages.cancelling} />
                  ) : (
                    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/anchor-is-valid
                    <a onClick={cancelUpload}>
                      <FormattedMessage {...messages.cancel} />
                    </a>
                  )}
                </div>
              </>
            ) : (
              <span className="center-middle">
                <Csv className="bulkupload-icon" />
                <span className="dropzone-label">
                  <FormattedMessage {...messages.dropZone} />
                </span>
              </span>
            )
          }
          label={<FormattedMessage {...messages.bulkLabel} />}
          name="dropzoneFile"
          onDrop={onFileDrop}
        />
        {validationIssues.length > 0 && (
          <div className="validation-issues">
            <PaginationTable
              className="compact error"
              columns={validationColumns({ intl })}
              pagination={{
                isClientPaging: true,
                count: validationIssues.length,
                startIndex: state.issuesPage * 10,
                perPage: 10
              }}
              onPaginateSelect={onSelectIssues}
              rows={[...validationIssues]}
              rowKey="readableValue"
            />
            <div className="validation-help">
              <FormattedMessage {...messages.validationHelp} />
            </div>
          </div>
        )}
        <div className="bulk-add-results">
          {created && created.length > 0 && (
            <div className="created-results">
              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
              <div className="additional-fields__label" onClick={() => toggleExpanded('created-results')}>
                <div className="additional-fields__row">
                  <TriangleDown2
                    // @ts-expect-error
                    className={cn('additional-fields__image', { closed: !state['created-results'] })}
                  />
                  <FormattedMessage {...messages.created} />
                  <span className="row-count">
                    <NumberAnimation speed={2000} value={created.length || 0} />
                  </span>
                </div>
                <hr className="additional-fields__divider" />
              </div>
              {/* @ts-expect-error */}
              <AnimateHeight duration={500} height={state['created-results'] ? 'auto' : 0}>
                <PaginationTable
                  className="compact"
                  columns={createdColumns({ baseUrl: baseUserUrl, intl })}
                  pagination={{
                    isClientPaging: true,
                    count: created.length,
                    startIndex: state.createdPage * 10,
                    perPage: 10
                  }}
                  onPaginateSelect={onSelectCreated}
                  rows={[...created]}
                  rowKey="readableValue"
                />
                {!isProcessing && (
                  <div>
                    {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/anchor-is-valid */}
                    <a onClick={saveCreatedList}>
                      <FormattedMessage {...messages.saveList} />
                    </a>
                  </div>
                )}
              </AnimateHeight>
            </div>
          )}
          {info && info.length > 0 && (
            <div className="info-results">
              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
              <div className="additional-fields__label" onClick={() => toggleExpanded('info-results')}>
                <div className="additional-fields__row">
                  <TriangleDown2
                    // @ts-expect-error
                    className={cn('additional-fields__image', { closed: !state['info-results'] })}
                  />
                  <FormattedMessage {...messages.info} />
                  <span className="row-count">
                    <NumberAnimation speed={2000} value={info.length || 0} />
                  </span>
                </div>
                <hr className="additional-fields__divider" />
              </div>
              {/* @ts-expect-error */}
              <AnimateHeight duration={500} height={state['info-results'] ? 'auto' : 0}>
                <PaginationTable
                  className="compact"
                  columns={userColumns({ intl })}
                  pagination={{
                    isClientPaging: true,
                    count: info.length,
                    startIndex: state.infoPage * 10,
                    perPage: 10
                  }}
                  onPaginateSelect={onSelectInfo}
                  rows={[...info]}
                  rowKey="readableValue"
                />
                {!isProcessing && (
                  <div>
                    {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/anchor-is-valid */}
                    <a onClick={saveInfoList}>
                      <FormattedMessage {...messages.saveList} />
                    </a>
                  </div>
                )}
              </AnimateHeight>
            </div>
          )}
          {failed && failed.length > 0 && (
            <div className="failed-results">
              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
              <div className="additional-fields__label" onClick={() => toggleExpanded('failed-results')}>
                <div className="additional-fields__row">
                  <TriangleDown2
                    // @ts-expect-error
                    className={cn('additional-fields__image', { closed: !state['failed-results'] })}
                  />
                  <FormattedMessage {...messages.failed} />
                  <span className="row-count">
                    <NumberAnimation speed={2000} value={failed.length || 0} />
                  </span>
                </div>
                <hr className="additional-fields__divider" />
              </div>
              {/* @ts-expect-error */}
              <AnimateHeight duration={500} height={state['failed-results'] ? 'auto' : 0}>
                <PaginationTable
                  className="compact"
                  columns={userColumns({ intl })}
                  pagination={{
                    isClientPaging: true,
                    count: failed.length,
                    startIndex: state.failedPage * 10,
                    perPage: 10
                  }}
                  onPaginateSelect={onSelectFailed}
                  rows={[...failed]}
                  rowKey="readableValue"
                />
                {!isProcessing && (
                  <div>
                    {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/anchor-is-valid */}
                    <a onClick={saveFailedList}>
                      <FormattedMessage {...messages.saveList} />
                    </a>
                  </div>
                )}
              </AnimateHeight>
            </div>
          )}
        </div>
      </Form>
    </Page>
  )
}

OrganizationUsersBulkAddPage.displayName = 'OrganizationUsersBulkAddPage'

OrganizationUsersBulkAddPage.propTypes = {
  actions: PropTypes.shape({
    bulkAddUsers: PropTypes.func.isRequired,
    setUsersToAddList: PropTypes.func.isRequired,
    fetchAttributes: PropTypes.func.isRequired,
    startProcessing: PropTypes.func.isRequired,
    finishedProcessing: PropTypes.func.isRequired,
    reset: PropTypes.func.isRequired,
    cancelUpload: PropTypes.func.isRequired
  }).isRequired,
  attributes: PropTypes.arrayOf(attributeShape),
  attributesHaveBeenFetched: PropTypes.bool.isRequired,
  usersToAdd: PropTypes.array,
  created: PropTypes.array,
  failed: PropTypes.array,
  info: PropTypes.array,
  csvData: PropTypes.array,
  isProcessingCsv: PropTypes.bool,
  orgAdminRole: PropTypes.object.isRequired,
  organizationId: PropTypes.string.isRequired
}

OrganizationUsersBulkAddPage.defaultProps = {
  attributes: [],
  usersToAdd: [],
  failed: [],
  created: [],
  info: [],
  csvData: [[]],
  isProcessingCsv: false
}

const DisconnectedOrganizationUsersBulkAddPage = OrganizationUsersBulkAddPage
export { /** @deprecated */ DisconnectedOrganizationUsersBulkAddPage as OrganizationUsersBulkAddPage }

function mapStateToProps(state: any) {
  return {
    attributes: getAttributesInOrder(state),
    attributesHaveBeenFetched: isRequestComplete(state, FETCH_ATTRIBUTES),
    failed: getFailedUsers(state),
    created: getCreatedUsers(state),
    info: getInfoUsers(state),
    usersToAdd: getUsersToAdd(state),
    csvData: getCsvData(state),
    isProcessingCsv: getIsProcessingCsv(state),
    orgAdminRole: getOrgAdminRole(state),
    organizationId: getCurrentOrganizationId(state)
  }
}

function mapDispatchToProps(dispatch: any) {
  return {
    actions: bindActionCreators(
      { bulkAddUsers, setUsersToAddList, fetchAttributes, startProcessing, finishedProcessing, reset, cancelUpload },
      dispatch
    )
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(DisconnectedOrganizationUsersBulkAddPage)
